import { useCallback } from 'react';
import { useAxiosInstance } from '@gov-nx/api/axios';
import { useUnauthorizedResponse } from '@gov-nx/api/errors/utils';
import { usePoQuery } from '@gov-nx/api/hooks/usePoQuery';
import { authMeQuery } from '@gov-nx/api/queries/authQuery';
import { useExtraConfigContext } from '@gov-nx/core/app/extra-config/ExtraConfigProvider';
import { useAuthEvents } from '@gov-nx/core/events/Auth/useAuthEvents';
import type { ApiCallback } from '@gov-nx/core/hooks/Api/useApiCallback';
import { useApiCallback } from '@gov-nx/core/hooks/Api/useApiCallback';
import { useStorageListener } from '@gov-nx/core/hooks/User/useStorageListener';
import { useAuthStore } from '@gov-nx/store/auth/store';

export const useAuthMeQuery = () => {
	const { setCallback, onError, onSuccess } = useApiCallback();
	const { extraConfig } = useExtraConfigContext();
	const { axiosInstance } = useAxiosInstance();
	const { userLogout } = useAuthEvents();
	const setUser = useAuthStore('setUser');
	const { persisKey } = extraConfig.store;
	const authKey = `${persisKey}:auth`; //TODO: move useStorageListener somewhere closer to store definition
	const { isUnauthorizedResponse } = useUnauthorizedResponse();

	useStorageListener(authKey, () => userLogout({ isBroadcast: true }), window);

	const meQuery = usePoQuery({
		queryKey: ['po-auth-me'],
		queryFn: () => authMeQuery(axiosInstance),
		onSuccess: (data) => {
			setUser({ loa: data.loa, typ_prihlaseni: data.typ_prihlaseni });
			onSuccess();
		},
		onError: (e) => {
			if (isUnauthorizedResponse(e) === false) {
				onError(e as Error);
			}
		},
		enabled: false,
		retry: 1,
	});

	const fetch = useCallback(
		async ({ ...args }: ApiCallback<undefined, Error>): Promise<void> => {
			setCallback({ ...args });
			await meQuery.refetch();
		},
		[meQuery, setCallback]
	);

	return { fetch };
};
